import type { QueuedTransactionPayload } from './webledgerOfflineState.js';

/**
 * An open form's unsaved values, kept in the tab's `sessionStorage` — so a tab
 * the browser discards and later restores reopens the form where its owner
 * was (ADR-0003 as amended 2026-10-22, decision 4).
 *
 * Only a discarded tab ever finds one. Leaving the page normally — closing
 * it, navigating, reloading — releases the form's hold and forgets this too
 * (`pagehide`), because that is somebody choosing to leave, and the browser's
 * own "Leave site?" has already asked them (`guardUnsaved`). A phone that
 * quietly discards a background tab runs no `pagehide`, and that is the case
 * this is for. A duplicated tab drops what it inherited (`claimTabIdentity`).
 */

const KEY = 'colplat:form:webledger';

export interface SavedForm {
  /** Which form: the short one on the wallets page, or the full editor. */
  form: 'quick' | 'full';
  /** The page it was open on; another page does not reopen it. */
  page: string;
  /** The transaction being edited, or null for a new one. */
  txId: string | null;
  payload: QueuedTransactionPayload;
  at: string;
}

function store(): Storage | undefined {
  try { return typeof sessionStorage === 'undefined' ? undefined : sessionStorage; } catch { return undefined; }
}

let forgettingOnExit = false;

export function rememberForm(form: Omit<SavedForm, 'at' | 'page'>): void {
  if (!forgettingOnExit && typeof window !== 'undefined') {
    forgettingOnExit = true;
    window.addEventListener('pagehide', forgetForm);
  }
  try {
    store()?.setItem(KEY, JSON.stringify({ ...form, page: location.pathname, at: new Date().toISOString() }));
  } catch { /* storage off or full: the form simply is not restorable */ }
}

export function forgetForm(): void {
  try { store()?.removeItem(KEY); } catch { /* storage off */ }
}

/** The form a discarded tab had open on this page, once — taking it forgets it. */
export function takeSavedForm(): SavedForm | undefined {
  let raw: string | null;
  try { raw = store()?.getItem(KEY) ?? null; } catch { raw = null; }
  if (!raw) return undefined;
  try {
    const saved = JSON.parse(raw) as SavedForm;
    if (saved.page !== location.pathname) return undefined;
    forgetForm();
    return saved;
  } catch {
    forgetForm();
    return undefined;
  }
}

/**
 * While a form has unsaved changes, leaving the page asks first — the
 * browser's own "Leave site?" (ADR-0003, 2026-10-22, decision 10). Closing a
 * tab used to lose them silently. Returns the way to stop asking.
 */
export function guardUnsaved(hasChanges: () => boolean): () => void {
  const handler = (event: BeforeUnloadEvent): void => {
    if (!hasChanges()) return;
    event.preventDefault();
    // Older browsers ask only when this is set; the text itself is never shown.
    event.returnValue = '';
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}
